import { css } from '@emotion/react';
import { faChevronRight } from '@fortawesome/pro-solid-svg-icons';
import {
  useCallback, useMemo, useRef, useState,
} from 'react';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { LabelInCircleComponent } from '~/_shared/baseComponents/labelInShape/labelInCircleComponent';
import {
  TooltipBehavior, TooltipComponent,
} from '~/_shared/baseComponents/tooltip/tooltip.component';
import { useResizeObserver } from '~/_shared/hooks/useResizeObserver';
import { type getColorsFromPalette } from '~/_shared/utils/colors/color.palette';
import { useTranslation } from '~/_shared/utils/hooks';
import {
  type ActiveBoundaryFillInfoEntities, concatEntities, useGetBoundaryFillEntities,
} from '~/sidebar/sidebarApps/mapTools/boundary/hooks/useGetBoundaryFillEntities';
import type { BoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';

type BoundaryFillSummaryTitleProps = Readonly<{
  boundaryTerritoryGroup: BoundaryTerritoryGroup;
  orderNumber: number;
  colors: ReturnType<typeof getColorsFromPalette>;
}>;

const wrapperStyles = css({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  textTransform: 'uppercase',
  fontSize: 14,
  minWidth: 0,
});

const numberInCircleStyles = css({
  fontSize: 14,
});

const ellipsisStyle = css({
  textOverflow: 'ellipsis',
  overflow: 'hidden',
  whiteSpace: 'nowrap',
});

export const BoundaryFillSummaryTitleComponent = ({
  boundaryTerritoryGroup,
  orderNumber,
  colors,
  ...props
}: BoundaryFillSummaryTitleProps) => {
  const [t] = useTranslation();
  const noFillEntities: ActiveBoundaryFillInfoEntities = useMemo(() => ({
    mainEntity: t('BoundaryFill.Manual'),
    shortenedMainEntity: t('BoundaryFill.Manual'),
  }), [t]);
  const getBoundaryFillEntities = useGetBoundaryFillEntities();
  const entities = getBoundaryFillEntities(boundaryTerritoryGroup) ?? noFillEntities;
  const [isOverflowing, setIsOverflowing] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const mainEntityRef = useRef<HTMLDivElement>(null);
  const subEntityRef = useRef<HTMLDivElement>(null);

  const checkOverflowVisibility = useCallback(() => {
    const mainEntityElement = mainEntityRef.current;
    const subEntityElement = subEntityRef.current;

    if (mainEntityElement && (
      (mainEntityElement.scrollWidth > mainEntityElement.clientWidth) || (mainEntityElement.scrollHeight > mainEntityElement.clientHeight)
    )) {
      setIsOverflowing(true);
    }
    else if (subEntityElement && (
      (subEntityElement.scrollWidth > subEntityElement.clientWidth) || (subEntityElement.scrollHeight > subEntityElement.clientHeight)
    )) {
      setIsOverflowing(true);
    }
    else {
      setIsOverflowing(false);
    }
  }, []);

  useResizeObserver({ onResize: checkOverflowVisibility, ref: wrapperRef });

  return (
    <TooltipComponent
      behavior={TooltipBehavior.ShowOnHover}
      tooltipContent={isOverflowing ? concatEntities(entities) : ''}
    >
      <div
        css={wrapperStyles}
        ref={wrapperRef}
        {...props}
      >
        <LabelInCircleComponent
          css={numberInCircleStyles}
          colors={{
            fontColor: colors.foreground,
            fillColor: colors.background,
          }}
          size={18}
        >
          {orderNumber}
        </LabelInCircleComponent>
        <div
          css={ellipsisStyle}
          ref={mainEntityRef}
        >
          {entities.mainEntity}
        </div>
        {entities.shortenedSubEntity && (
          <FontAwesomeIcon
            icon={faChevronRight}
            size="lg"
          />
        )}
        {entities.shortenedSubEntity && (
          <div
            css={ellipsisStyle}
            ref={subEntityRef}
          >
            {entities.shortenedSubEntity}
          </div>
        )}
      </div>
    </TooltipComponent>
  );
};
